<template>
  <div class="enrollment-new pa-10">
    <title-page>
      {{ $t('onboarding.create-enrollment') }}
    </title-page>

    <v-card class="pa-6">
      <v-form ref="form" v-model="valid">
        <!-- Type Selection (Required) -->
        <v-radio-group
          v-model="form.type"
          row
          :rules="[rules.required]"
          class="mb-4"
          hide-details
        >
          <template v-slot:label>
            <span class="font-weight-bold">{{ $t('type') }} *</span>
          </template>
          <v-radio
            :label="$t('onboarding.organization')"
            value="organization"
            color="blue"
          ></v-radio>
          <v-radio
            :label="$t('onboarding.customer')"
            value="customer"
            color="purple"
          ></v-radio>
        </v-radio-group>

        <!-- Email -->
        <v-text-field
          v-model="form.email"
          :label="$t('email') + ' *'"
          :rules="[rules.required, rules.email]"
          :loading="checkingEmail"
          outlined
          dense
          hide-details
          required
        ></v-text-field>

        <!-- Existing Customer Alert -->
        <v-alert
          v-if="showExistingCustomerAlert && existingCustomer && form.type === 'customer'"
          type="info"
          dense
          outlined
          class="mt-4"
        >
          <div class="d-flex flex-column">
            <div class="font-weight-bold mb-2">
              {{ $t('onboarding.customer-already-exists') }}
            </div>
            <div class="text-body-2 mb-2">
              <router-link
                :to="{name: 'customer.detail', params: {id: existingCustomer.id}}"
                target="_blank"
                class="font-weight-medium d-inline-flex align-center"
              >
                {{ existingCustomer.firstname }} {{ existingCustomer.lastname }}
                <v-icon small class="ml-1">mdi-open-in-new</v-icon>
              </router-link>
              <span v-if="existingCustomer.evcoId" class="text--secondary ml-2">({{ existingCustomer.evcoId }})</span>
            </div>

            <!-- Active payment methods breakdown -->
            <div v-if="existingCustomerPaymentMethods.total > 0" class="text-body-2 mb-2">
              <v-icon small color="success" class="mr-1">mdi-credit-card-check</v-icon>
              <span class="success--text">
                {{ $t('onboarding.customer-has-payment-methods', { count: existingCustomerPaymentMethods.total }) }}
                <template v-if="existingCustomerPaymentMethods.cards > 0 || existingCustomerPaymentMethods.sepa > 0">
                  (
                  <template v-if="existingCustomerPaymentMethods.cards > 0">
                    {{ existingCustomerPaymentMethods.cards }} {{ $t('onboarding.payment-type-card') }}
                  </template>
                  <template v-if="existingCustomerPaymentMethods.cards > 0 && existingCustomerPaymentMethods.sepa > 0">, </template>
                  <template v-if="existingCustomerPaymentMethods.sepa > 0">
                    {{ existingCustomerPaymentMethods.sepa }} SEPA
                  </template>
                  )
                </template>
              </span>
            </div>

            <!-- Existing memberships - clickable to attach -->
            <template v-if="existingCustomerMemberships && existingCustomerMemberships.length > 0">
              <div class="text-caption text--secondary mb-1">
                {{ $t('onboarding.customer-already-member-of') }}
                <span class="text-caption font-italic">({{ $t('onboarding.click-to-attach') }})</span>:
              </div>
              <div class="d-flex flex-wrap gap-1">
                <v-chip
                  v-for="membership in existingCustomerMemberships"
                  :key="membership.id"
                  x-small
                  color="blue"
                  dark
                  class="cursor-pointer"
                  @click="attachFromMembership(membership)"
                >
                  {{ membership.organizationName || membership.organization?.name || 'Organization' }}
                </v-chip>
              </div>
            </template>
          </div>
        </v-alert>

        <!-- Signer identity (for customers only) -->
        <template v-if="form.type === 'customer'">
          <v-divider class="my-4"></v-divider>
          <h3 class="text-subtitle-1 mb-3">{{ $t('onboarding.signer-identity') }}</h3>
          <v-alert type="info" dense outlined class="mb-4">
            <div class="text-caption">
              {{ $t('onboarding.signer-identity-hint') }}
            </div>
          </v-alert>

          <v-row>
            <v-col>
              <v-text-field
                v-model="form.firstName"
                :label="firstNameLabel"
                :rules="firstNameRules"
                outlined
                dense
                placeholder="Jean"
              ></v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                v-model="form.lastName"
                :label="lastNameLabel"
                :rules="lastNameRules"
                outlined
                dense
                placeholder="Dupont"
              ></v-text-field>
            </v-col>
            <v-col>
              <customer-select
                v-model="form.evcoId"
                :label="$t('evco-id')"
                item-value="evcoId"
                outlined
                dense
                selectionOneLine
                clearable
              />
            </v-col>
          </v-row>

          <!-- Attach to Organization (for customers only) -->
          <v-divider class="my-4"></v-divider>
          <h3 class="text-subtitle-1 mb-3">{{ $t('onboarding.membership') }}</h3>
          <v-alert type="info" dense outlined class="mb-4">
            <div class="text-caption">
              {{ $t('onboarding.attach-to-organization-hint') }}
            </div>
          </v-alert>

          <v-row>
            <v-col cols="12" md="6">
              <organization-select
                ref="attachOrgSelect"
                v-model="form.attachToOrganization"
                item-value="accountNumber"
                :label="$t('onboarding.attach-to-organization')"
                outlined
                dense
                clearable
                @input="onOrganizationSelected"
              />
            </v-col>
          </v-row>

          <v-row v-if="form.attachToOrganization">
            <v-col cols="12" md="6">
              <v-select
                v-model="form.memberType"
                :items="availableMemberTypes"
                :label="$t('onboarding.member-type')"
                :loading="loadingMemberTypes"
                :disabled="loadingMemberTypes || availableMemberTypes.length === 0"
                item-text="name"
                item-value="name"
                outlined
                dense
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="selectedMemberGroups"
                :items="availableMemberGroups"
                :label="$t('onboarding.member-groups')"
                :loading="loadingMemberGroups"
                :disabled="loadingMemberGroups || availableMemberGroups.length === 0"
                item-text="name"
                item-value="name"
                outlined
                dense
                clearable
                multiple
                chips
                small-chips
                deletable-chips
              ></v-select>
            </v-col>
          </v-row>
        </template>

        <!-- Company/Organization information -->
        <v-divider class="my-4"></v-divider>
        <h3 class="text-subtitle-1 mb-3">{{ $t('onboarding.company-information') }}</h3>

        <v-row>
          <v-col>
            <v-text-field
              v-model="form.companyName"
              :label="companyNameLabel"
              :rules="companyNameRules"
              outlined
              dense
            ></v-text-field>
          </v-col>

          <v-col v-if="form.type === 'organization'">
            <organization-select
              v-model="form.accountNumber"
              :label="$t('account-number')"
              item-value="accountNumber"
              outlined
              dense
              clearable
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-text-field
              v-model="form.siret"
              :label="$t('siret')"
              outlined
              hide-details
              dense
            ></v-text-field>
          </v-col>

          <v-col>
            <v-text-field
              v-model="form.vatNumber"
              :label="$t('vat-number')"
              outlined
              hide-details
              dense
            ></v-text-field>
          </v-col>
        </v-row>


        <!-- Address -->
        <v-divider class="my-4"></v-divider>
        <h3 class="text-subtitle-1 mb-3">{{ $t('address') }}</h3>

        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="form.address.street"
              :label="$t('street')"
              hide-details
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-text-field
              v-model="form.address.city"
              :label="$t('city')"
              outlined
              hide-details
              dense
            ></v-text-field>
          </v-col>
          <v-col>
            <v-text-field
              v-model="form.address.zipcode"
              :label="$t('zipcode')"
              outlined
              hide-details
              dense
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <available-select
                v-model="form.address.country"
                :label="$t('country')"
                outlined
                dense
                type="country"
                clearable
            />
          </v-col>
        </v-row>





        <!-- Phone -->


        <!-- Communication language -->
        <v-divider class="my-4"></v-divider>

        <v-row>
          <v-col>
            <v-text-field
                v-model="form.phone"
                :label="$t('phone')"
                outlined
                hide-details
                dense
            ></v-text-field>
          </v-col>

          <v-col>
            <v-select
                v-model="form.locale"
                :items="localeOptions"
                :label="$t('communication-language')"
                outlined
                hide-details
                dense
                :hint="$t('onboarding.locale-hint')"
                persistent-hint
            ></v-select>
          </v-col>
        </v-row>





      </v-form>

      <v-divider class="my-4"></v-divider>

      <div class="d-flex justify-space-between">
        <v-btn text small @click="$router.back()">
          <v-icon left small>mdi-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          color="primary"
          small
          :loading="loading"
          :disabled="!valid"
          @click="createEnrollment"
        >
          <v-icon left small>mdi-check</v-icon>
          {{ $t('create') }}
        </v-btn>
      </div>
    </v-card>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <v-icon left color="success">mdi-check-circle</v-icon>
          {{ $t('onboarding.enrollment-created') }}
        </v-card-title>
        <v-card-text>
          <p class="mb-4">{{ $t('onboarding.enrollment-created-message') }}</p>

          <!-- Public link -->
          <v-text-field
            :value="publicLink"
            :label="$t('onboarding.public-link')"
            outlined
            dense
            readonly
            append-icon="mdi-content-copy"
            @click:append="copyPublicLink"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="closeSuccessDialog" :disabled="loadingSendEmail">
            {{ $t('close') }}
          </v-btn>
          <v-btn color="primary" small @click="sendEmailNow" :loading="loadingSendEmail">
            <v-icon left small>mdi-send</v-icon>
            {{ $t('onboarding.send-email-now') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PaymentOnboardingRepository from '@repository/PaymentOnboardingRepository'
import MemberTypeRepository from '@repository/MemberTypeRepository'
import MemberGroupRepository from '@repository/MemberGroupRepository'
import CustomerRepository from '@repository/CustomerRepository'
import PaymentMethodRepository from '@repository/PaymentMethodRepository'
import OrganizationRepository from '@repository/OrganizationRepository'
import OrganizationSelect from "@blocks/Select/OrganizationSelect.vue";
import CommonMixin from '@/components/mixins/CommonMixin'
import CustomerSelect from "@blocks/Select/CustomerSelect.vue";
import AvailableSelect from "@blocks/Select/AvailableSelect.vue";
import debounce from 'lodash/debounce';

export default {
  name: 'EnrollmentNew',

  mixins: [CommonMixin],

  components: {
    AvailableSelect,
    OrganizationSelect,
    CustomerSelect,
  },

  data() {
    return {
      valid: false,
      loading: false,
      loadingSendEmail: false,
      successDialog: false,
      createdEnrollment: null,
      // Organization membership selection
      selectedOrganizationId: null,
      selectedMemberGroups: [],
      availableMemberTypes: [],
      availableMemberGroups: [],
      loadingMemberTypes: false,
      loadingMemberGroups: false,
      // Existing customer check
      existingCustomer: null,
      existingCustomerMemberships: [],
      existingCustomerPaymentMethods: { total: 0, cards: 0, sepa: 0 },
      checkingEmail: false,
      showExistingCustomerAlert: false,
      form: {
        type: 'organization', // Default to organization
        email: '',
        companyName: '',
        firstName: '',
        lastName: '',
        accountNumber: '',
        evcoId: '',
        siret: '',
        vatNumber: '',
        phone: '',
        locale: 'fr',
        address: {
          street: '',
          city: '',
          zipcode: '',
          country: 'FR'
        },
        // Customer-only fields for organization membership
        attachToOrganization: '',
        memberType: '',
        memberGroups: ''
      },
      localeOptions: [
        { text: 'Français', value: 'fr' },
        { text: 'English', value: 'en' },
        { text: 'Nederlands', value: 'nl' }
      ],
      rules: {
        required: v => !!v || this.$t('rules.field-required'),
        email: v => !v || /.+@.+\..+/.test(v) || this.$t('rules.email-not-valid')
      }
    }
  },

  computed: {
    publicLink() {
      if (!this.createdEnrollment?.token) return ''
      return `${window.location.origin}/onboarding?token=${this.createdEnrollment.token}&locale=${this.createdEnrollment.locale}`
    },

    // Labels with conditional required indicator (*)
    firstNameLabel() {
      return this.form.type === 'customer'
        ? this.$t('firstname') + ' *'
        : this.$t('firstname')
    },
    lastNameLabel() {
      return this.form.type === 'customer'
        ? this.$t('lastname') + ' *'
        : this.$t('lastname')
    },
    companyNameLabel() {
      return this.form.type === 'organization'
        ? this.$t('company-name') + ' *'
        : this.$t('company-name')
    },

    // Conditional validation rules
    firstNameRules() {
      return this.form.type === 'customer' ? [this.rules.required] : []
    },
    lastNameRules() {
      return this.form.type === 'customer' ? [this.rules.required] : []
    },
    companyNameRules() {
      return this.form.type === 'organization' ? [this.rules.required] : []
    }
  },

  watch: {
    'form.type'(newType) {
      // Clear incompatible fields when type changes
      if (newType === 'organization') {
        // Clear customer-specific fields
        this.form.evcoId = ''
        this.form.attachToOrganization = ''
        this.form.memberType = ''
        this.form.memberGroups = ''
        this.selectedOrganizationId = null
        this.selectedMemberGroups = []
        this.availableMemberTypes = []
        this.availableMemberGroups = []
        // Clear existing customer alert
        this.existingCustomer = null
        this.existingCustomerMemberships = []
        this.showExistingCustomerAlert = false
      } else if (newType === 'customer') {
        // Clear organization-specific fields
        this.form.accountNumber = ''
        // Check email if already filled
        if (this.form.email) {
          this.checkExistingCustomer(this.form.email)
        }
      }
    },
    'form.email'(newEmail) {
      // Only check if type is customer
      if (this.form.type === 'customer' && newEmail) {
        this.debouncedCheckEmail(newEmail)
      } else {
        this.existingCustomer = null
        this.existingCustomerMemberships = []
        this.existingCustomerPaymentMethods = { total: 0, cards: 0, sepa: 0 }
        this.showExistingCustomerAlert = false
      }
    }
  },

  created() {
    // Create debounced version of email check
    this.debouncedCheckEmail = debounce(this.checkExistingCustomer, 500)
  },

  methods: {
    async checkExistingCustomer(email) {
      if (!email || !/.+@.+\..+/.test(email)) {
        this.existingCustomer = null
        this.existingCustomerMemberships = []
        this.existingCustomerPaymentMethods = { total: 0, cards: 0, sepa: 0 }
        this.showExistingCustomerAlert = false
        return
      }

      this.checkingEmail = true
      try {
        // Search for customer with this exact email
        const response = await CustomerRepository.search({
          searchText: email,
          offset: 0,
          limit: 10,
          checkMembership: true
        })

        // Find exact email match (case insensitive)
        const customer = response.items?.find(c =>
          c.email && c.email.toLowerCase() === email.toLowerCase()
        )

        if (customer) {
          this.existingCustomer = customer
          this.existingCustomerMemberships = customer.members || []
          this.showExistingCustomerAlert = true

          // Check for active payment methods (exclude deferred)
          try {
            const pmResponse = await PaymentMethodRepository.listPaymentMethods({
              customerId: customer.id,
              statuses: ['active', 'succeeded'],
              limit: 100
            })
            // Filter out deferred payment methods and count by type
            const methods = (pmResponse.items || []).filter(pm => pm.type !== 'deferred')
            const cards = methods.filter(pm => pm.type === 'card').length
            const sepa = methods.filter(pm => pm.type === 'sepa_debit').length
            this.existingCustomerPaymentMethods = {
              total: methods.length,
              cards,
              sepa
            }
          } catch (pmErr) {
            console.error('Failed to load payment methods:', pmErr)
            this.existingCustomerPaymentMethods = { total: 0, cards: 0, sepa: 0 }
          }

          // Auto-fill customer data since we can't create duplicate emails
          await this.fillFromExistingCustomer()
        } else {
          this.existingCustomer = null
          this.existingCustomerMemberships = []
          this.existingCustomerPaymentMethods = { total: 0, cards: 0, sepa: 0 }
          this.showExistingCustomerAlert = false
        }
      } catch (err) {
        console.error('Failed to check existing customer:', err)
        this.existingCustomer = null
        this.existingCustomerMemberships = []
        this.existingCustomerPaymentMethods = { total: 0, cards: 0, sepa: 0 }
        this.showExistingCustomerAlert = false
      } finally {
        this.checkingEmail = false
      }
    },

    async fillFromExistingCustomer() {
      if (!this.existingCustomer) return

      // Fill form with existing customer data
      this.form.firstName = this.existingCustomer.firstname || ''
      this.form.lastName = this.existingCustomer.lastname || ''
      this.form.evcoId = this.existingCustomer.evcoId || ''
      this.form.phone = this.existingCustomer.phone || ''
      this.form.companyName = this.existingCustomer.company || ''

      // Fill address if available
      if (this.existingCustomer.address) {
        this.form.address.street = this.existingCustomer.address.street || ''
        this.form.address.city = this.existingCustomer.address.city || ''
        this.form.address.zipcode = this.existingCustomer.address.zipcode || this.existingCustomer.address.postalCode || ''
        this.form.address.country = this.existingCustomer.address.country || 'FR'
      }

      // Auto-attach if only one organization membership
      if (this.existingCustomerMemberships && this.existingCustomerMemberships.length === 1) {
        await this.attachFromMembership(this.existingCustomerMemberships[0])
      }

      this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('onboarding.customer-data-filled'))
    },

    dismissExistingCustomerAlert() {
      this.showExistingCustomerAlert = false
    },

    async attachFromMembership(membership) {
      // Get organization details to find accountNumber
      const organizationId = membership.organizationId || membership.organization?.id
      if (!organizationId) {
        console.error('No organization ID found in membership')
        return
      }

      try {
        const org = await OrganizationRepository.get(organizationId, true)
        if (org && org.accountNumber) {
          // Set the organization in the form
          this.form.attachToOrganization = org.accountNumber
          this.selectedOrganizationId = organizationId

          // Load member types and groups for this organization
          await Promise.all([
            this.loadMemberTypes(organizationId),
            this.loadMemberGroups(organizationId)
          ])

          // Pre-select the member type if available in membership
          if (membership.type?.name) {
            this.form.memberType = membership.type.name
          }

          // Pre-select the groups if available in membership
          if (membership.groups && membership.groups.length > 0) {
            this.selectedMemberGroups = membership.groups.map(g => g.name || g)
          }

          this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('onboarding.organization-attached'))
        }
      } catch (err) {
        console.error('Failed to get organization details:', err)
        this.notifyError(err)
      }
    },

    async onOrganizationSelected(accountNumber) {
      // Reset member type and groups when organization changes
      this.form.memberType = ''
      this.form.memberGroups = ''
      this.selectedMemberGroups = []
      this.availableMemberTypes = []
      this.availableMemberGroups = []
      this.selectedOrganizationId = null

      if (!accountNumber) {
        return
      }

      // Find the organization object from the select's items to get the ID
      const selectComponent = this.$refs.attachOrgSelect
      if (selectComponent && selectComponent.items) {
        const org = selectComponent.items.find(item => item.accountNumber === accountNumber)
        if (org) {
          this.selectedOrganizationId = org.id

          // Load member types and groups for this organization
          await Promise.all([
            this.loadMemberTypes(org.id),
            this.loadMemberGroups(org.id)
          ])
        }
      }
    },

    async loadMemberTypes(organizationId) {
      this.loadingMemberTypes = true
      try {
        const response = await MemberTypeRepository.search({
          organizationId: organizationId,
          offset: 0,
          limit: 100
        })
        this.availableMemberTypes = response.items || []

        // Auto-select if only one type available
        if (this.availableMemberTypes.length === 1) {
          this.form.memberType = this.availableMemberTypes[0].name
        }
      } catch (err) {
        console.error('Failed to load member types:', err)
        this.availableMemberTypes = []
      } finally {
        this.loadingMemberTypes = false
      }
    },

    async loadMemberGroups(organizationId) {
      this.loadingMemberGroups = true
      try {
        const response = await MemberGroupRepository.search({
          organizationId: organizationId,
          offset: 0,
          limit: 100
        })
        this.availableMemberGroups = response.items || []
      } catch (err) {
        console.error('Failed to load member groups:', err)
        this.availableMemberGroups = []
      } finally {
        this.loadingMemberGroups = false
      }
    },

    async createEnrollment() {
      if (!this.$refs.form.validate()) return

      this.loading = true

      try {
        // Prepare form data
        const formData = { ...this.form }

        // Convert selected member groups array to comma-separated string
        if (this.selectedMemberGroups && this.selectedMemberGroups.length > 0) {
          formData.memberGroups = this.selectedMemberGroups.join(', ')
        }

        const data = await PaymentOnboardingRepository.createEnrollment(formData)
        // Backend returns { enrollmentId, token, status, tokenExpiration }
        // Normalize to { id, token, status, tokenExpiration }
        this.createdEnrollment = {
          id: data.enrollmentId,
          token: data.token,
          status: data.status,
          tokenExpiration: data.tokenExpiration
        }
        this.successDialog = true
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    copyPublicLink() {
      navigator.clipboard.writeText(this.publicLink).then(() => {
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('onboarding.link-copied'))
      })
    },

    async sendEmailNow() {
      if (!this.createdEnrollment?.id) {
        this.$dialog?.notify?.error && this.$dialog.notify.error('No enrollment ID found')
        return
      }

      try {
        this.loadingSendEmail = true
        await PaymentOnboardingRepository.sendEnrollmentEmail(this.createdEnrollment.id)
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('onboarding.email-sent'))
        this.closeSuccessDialog()
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loadingSendEmail = false
      }
    },

    closeSuccessDialog() {
      this.successDialog = false
      this.$router.push({ name: 'payment-onboarding.enrollments' })
    }
  }
}
</script>

<style lang="scss">
.enrollment-new {}
</style>
